import { defineStore } from "pinia";
import ExtensionService from "../services/extension-service";
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  SELECTED_MEMBERSHIP_KEY,
  VIEW_AS_STANDARD_KEY,
} from "../config/constants";
import {
  decodeJwtPayload,
  ExtensionDashboardMessageType,
  isAccessTokenFrozen,
  isAccessTokenOnboarding,
  Role,
} from "tf-api-client";
import { User } from "tf-api-client";
import api from "../api";
import { sessionEvents } from "../services/session-events";
import posthog from "posthog-js";

// A restricted token (`frozen`: read-only billing view; `onboarding`: card
// step only) must never reach the browser extension: push a logout instead so
// the extension does not keep a session it cannot use. The refresh that lifts
// the restriction pushes the new tokens.
const pushSessionToExtension = (accessToken: string, refreshToken: string) => {
  if (isAccessTokenFrozen(accessToken) || isAccessTokenOnboarding(accessToken)) {
    return ExtensionService.sendMessage({
      type: ExtensionDashboardMessageType.Logout,
    });
  }

  return ExtensionService.sendMessage({
    type: ExtensionDashboardMessageType.SetTokens,
    accessToken,
    refreshToken,
  });
};

export const useAuthStore = defineStore("auth", {
  state: () => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    let user: User.UserSelfResponse;
    let selectedMembership: User.Membership;

    if (token && refreshToken) {
      pushSessionToExtension(token, refreshToken);
    }

    return {
      user: user!,
      selectedMembership: selectedMembership!,
      token: localStorage.getItem(ACCESS_TOKEN_KEY) || null,
      lastSync: 0,
      viewAsStandard: localStorage.getItem(VIEW_AS_STANDARD_KEY) === "true",
      // Error code (`errors.<code>`) explaining a forced sign-out, shown once
      // on the login screen.
      logoutReason: null as string | null,
      // Organizations known to be suspended while the session itself is
      // still unrestricted (e.g. a Managed org viewed by its MSP admin).
      frozenOrgIds: [] as string[],
    };
  },

  getters: {
    canViewAsStandard: (state) =>
      state.selectedMembership?.role === Role.OrgAdmin,

    // Role the UI is rendered for. Org admins can preview the dashboard as a
    // standard user; this only changes what the UI shows, not API permissions.
    effectiveRole(state): Role {
      if (this.canViewAsStandard && state.viewAsStandard) {
        return Role.Standard;
      }
      return state.selectedMembership?.role;
    },
    // Session-suspended: the token was issued to a user of a frozen primary
    // organization and only allows the read-only billing view. Derived from
    // the token so it can never go stale.
    suspended: (state): boolean => isAccessTokenFrozen(state.token),

    // Self-service onboarding: the token was issued before a card was
    // confirmed and only grants the card step (plus settings, upgrade and
    // cancel). Derived from the token, like `suspended`.
    onboarding: (state): boolean => isAccessTokenOnboarding(state.token),

    // The organization the token was issued for (the user's primary one).
    // Not the same as `selectedMembership`, which may be any membership.
    tokenOrgId: (state): string | null =>
      (state.token && decodeJwtPayload(state.token)?.org_id) || null,
  },

  actions: {
    async init() {
      if (this.token) {
        try {
          this.applyUser(await api.user.self());
        } catch (error) {
          console.error("Failed to fetch user data:", error);
          this.logout();
        }
      }
    },

    // Stores the user and resolves the selected membership: the saved one,
    // else the primary. `keepSelection` (a refresh for the same user) keeps
    // the membership currently selected in this session first; a login must
    // not, as the in-memory selection may still be a previous user's.
    applyUser(user: User.UserSelfResponse, keepSelection = false) {
      this.user = user;

      const preferredId =
        (keepSelection && this.selectedMembership?.organizationId) ||
        localStorage.getItem(SELECTED_MEMBERSHIP_KEY);
      const preferred = preferredId
        ? user.memberships.find((m) => m.organizationId === preferredId)
        : null;

      this.selectedMembership =
        preferred || user.memberships.find((membership) => membership.primary)!;

      posthog.identify(user.id, user);

      this.syncWithExtension();
    },

    // Re-issues the token pair and reloads the user. Needed when the user's
    // memberships change on the server (e.g. after creating a Managed
    // organization): the `perms` claim of the current access token, which the
    // API authorizes against, and `user.memberships`, which feeds the
    // organization switcher, only know about the new organization afterwards.
    // The token refresh goes through the SDK, whose `tokensRefreshed` event
    // updates the token held by this store.
    async refreshSession() {
      await api.refreshSession();
      this.applyUser(await api.user.self(), true);
    },

    selectOrganization(organizationId: string) {
      const membership = this.user.memberships.find(
        (membership) => membership.organizationId === organizationId,
      );

      if (membership) {
        // What was cached for that organization may be old: the session
        // handler has it refetched, and a suspension seen earlier is read
        // again rather than assumed.
        this.clearOrgFrozen(organizationId);
        void sessionEvents.emit({ type: "organizationSelected", organizationId });
        this.selectedMembership = membership;
        localStorage.setItem(SELECTED_MEMBERSHIP_KEY, organizationId);
      } else {
        console.warn(
          `Membership with ID ${organizationId} not found in user memberships`,
        );
      }
    },

    setViewAsStandard(enabled: boolean) {
      this.viewAsStandard = enabled;
      localStorage.setItem(VIEW_AS_STANDARD_KEY, String(enabled));
    },

    async setTokens(accessToken: string, refreshToken: string) {
      this.token = accessToken;
      this.logoutReason = null;
      this.frozenOrgIds = [];
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

      await this.init();
      await this.syncWithExtension();
    },

    // The SDK already stored the refreshed pair in localStorage; the store's
    // own copy of the access token has to follow, otherwise `suspended`,
    // `onboarding` and `tokenOrgId` would be derived from a stale token.
    async applyRefreshedTokens(accessToken: string) {
      this.token = accessToken;
      // a refresh can change the token's restriction: never skip the push
      this.lastSync = 0;
      await this.syncWithExtension();
    },

    async syncWithExtension() {
      const now = Date.now();
      if (now - this.lastSync < 2000) {
        return;
      }
      this.lastSync = now;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (this.token && refreshToken) {
        await pushSessionToExtension(this.token, refreshToken);
      }
    },

    markOrgFrozen(organizationId: string) {
      if (!this.frozenOrgIds.includes(organizationId)) {
        this.frozenOrgIds.push(organizationId);
      }
    },

    clearOrgFrozen(organizationId: string) {
      this.frozenOrgIds = this.frozenOrgIds.filter(
        (id) => id !== organizationId,
      );
    },

    // `reason` is an `errors.<reason>` key for the login screen. Without one
    // (a manual logout) a reason set earlier in the same sign-out is kept.
    logout(reason?: string) {
      this.token = null;
      this.lastSync = 0;
      this.viewAsStandard = false;
      this.frozenOrgIds = [];
      if (reason) {
        this.logoutReason = reason;
      }
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(SELECTED_MEMBERSHIP_KEY);
      localStorage.removeItem(VIEW_AS_STANDARD_KEY);

      ExtensionService.sendMessage({
        type: ExtensionDashboardMessageType.Logout,
      });
      posthog.reset();
    },

    isLoggedIn() {
      return !!this.token;
    },
  },
});
